import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Button } from 'Atoms/Buttons/Buttons.tsx';
import { Menu, MenuSection } from 'Molecules/Menu/Menu.tsx';
import { HStack, Divider, Box, MenuButton } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Typography } from 'Tokens/index.ts';
import { SelectedAssessmentOption } from './TaskManagement.tsx';
import { CheckIcon } from 'Tokens/Icons/Status.tsx';
import { TaskCompanyOption } from '../hooks/TaskList.hooks.ts';
import { useParams } from 'react-router-dom';

interface TaskListAssessmentFilterProps {
  selectedAssessment?: SelectedAssessmentOption;
  setSelectedAssessment: (assessment: SelectedAssessmentOption) => void;
  companyOptions: TaskCompanyOption[];
  isHomePage?: boolean;
}

export const TaskListAssessmentFilter: React.FC<TaskListAssessmentFilterProps> = ({
  selectedAssessment,
  setSelectedAssessment,
  companyOptions,
  isHomePage,
}) => {
  const [expandedCompanyIds, setExpandedCompanyIds] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  const { companyId = '' } = useParams();

  const filteredCompanyOptions = useMemo(
    () => companyOptions.filter((company) => company.assessments.length > 0),
    [companyOptions]
  );

  useEffect(() => {
    const currentCompany =
      filteredCompanyOptions?.find((c) => c.id === companyId) ?? companyOptions?.[0];
    const latestAssessment = currentCompany?.assessments?.[0];

    if (selectedAssessment === undefined && !!filteredCompanyOptions.length)
      setSelectedAssessment({
        id: latestAssessment?.id,
        name: latestAssessment?.name,
        frameworkRef: latestAssessment?.frameworkRef,
        companyData: { id: currentCompany?.id, name: currentCompany?.name },
      });
    if (isHomePage) {
      setExpandedCompanyIds([companyId]);
    }
  }, [filteredCompanyOptions, companyId, selectedAssessment, isHomePage]);

  const handleToggleExpand = (companyId: string) => {
    setExpandedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };

  const companyActions = filteredCompanyOptions.flatMap((company) => {
    const isExpanded = expandedCompanyIds.includes(company.id);
    const companyActions = [
      ...(!isHomePage
        ? [
            {
              id: `company-${company.id}`,
              render: () => {
                return (
                  <HStack cursor="pointer" width="100%" justifyContent="space-between">
                    <Typography variant="body" ml={2} userSelect="none">
                      {company.name}
                    </Typography>
                    <Box>{isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}</Box>
                  </HStack>
                );
              },
              onClick: () => handleToggleExpand(company.id),
              closeOnClick: false,
            },
          ]
        : []),
      ...(isExpanded
        ? company.assessments.map((assessment) => ({
            id: `assessment-${assessment.id}`,
            onClick: () => {
              setSelectedAssessment({
                id: assessment.id,
                name: assessment.name,
                frameworkRef: assessment?.frameworkRef ?? ' ',
                companyData: { id: company.id, name: company.name },
              });
              setMenuOpen(false);
            },
            render: () => {
              const isSelected = selectedAssessment?.id === assessment.id;
              return (
                <HStack
                  key={assessment.id}
                  spacing={4}
                  align="center"
                  paddingLeft={isHomePage ? '' : '16px'}
                  justifyContent="space-between"
                  width="100%"
                  cursor="pointer"
                >
                  <Typography
                    variant={isHomePage ? 'bodyStrong' : 'body'}
                    color={isSelected ? 'text.selected' : undefined}
                  >
                    {assessment.name}
                  </Typography>
                  {isSelected && <CheckIcon color="text.selected" />}
                </HStack>
              );
            },
          }))
        : []),
    ];
    return companyActions;
  });

  const sections: MenuSection[] = [
    {
      title: isHomePage ? 'Assessments' : 'Companies and assessments',
      description: isHomePage
        ? 'Tasks only applicable to ESRS and Multi-framework assessments.'
        : undefined,
      actions: companyActions,
    },
  ];

  return (
    <Menu
      customMenuButton={
        <MenuButton
          as={Button}
          variant="ghost"
          p="4px"
          onClick={(e) => e.stopPropagation()}
          bgColor={menuOpen ? 'bg.pressed' : ''}
          _hover={{ bgColor: 'bg.hover' }}
        >
          <HStack
            spacing="8px"
            borderRadius="md"
            alignItems="center"
            minW={isHomePage ? 'fit-content' : '180px'}
            justifyContent="flex-start"
            cursor="pointer"
            onClick={() => {
              setMenuOpen(!menuOpen);
            }}
          >
            {selectedAssessment ? (
              isHomePage ? (
                <Typography variant="h3">{selectedAssessment?.name}</Typography>
              ) : (
                <HStack spacing="8px" align="start">
                  <Typography variant="h3">
                    {selectedAssessment ? selectedAssessment.companyData.name : 'Select assessment'}
                  </Typography>
                  <Divider orientation="vertical" height="24px" w="2px" />
                  <Typography variant="h3">{selectedAssessment?.name}</Typography>
                </HStack>
              )
            ) : (
              <Typography variant="body" color="gray.400">
                Select assessment
              </Typography>
            )}
            <ChevronDownIcon boxSize="16px" />
          </HStack>
        </MenuButton>
      }
      sections={sections}
      size="lg"
      isOpen={menuOpen}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      closeOnSelect={false}
      maxHeight="400px"
    />
  );
};
